<template>
  <div class="face-id-tab">
    <BaseTabs
      :tabs="tabOptions"
      :active-tab="activeTab"
      class="mb-4"
      @change:tab="activeTab = $event"
    />

    <Tabs v-model="activeTab" class="w-full">
      <TabsContent value="overview">
        <div v-if="loading" class="loading">
          <Spinner size="default" />
        </div>
        <template v-else>
          <div
            v-if="overviewImages.length"
            class="mb-10 grid grid-cols-4 gap-2.5"
          >
            <FacePhotoCell
              v-for="(image, index) in overviewImages"
              :key="image.id"
              :image="image"
              :number="index + 1"
              :can-see-photo="isOwnUser"
              :revealable="isAdmin && !isOwnUser"
              @photo-failed="onPhotoFailed"
            />
          </div>

          <InformationLayout :show-header="false" :sections="infoSections" />

          <div class="mt-4 grid grid-cols-3 gap-2.5">
            <div class="mini-stat">
              <div class="mini-k">{{ $t("Всего попыток") }}</div>
              <div class="mini-v">{{ overviewStats.total }}</div>
            </div>
            <div class="mini-stat">
              <div class="mini-k">{{ $t("Успешных") }}</div>
              <div class="mini-v text-green-600">
                {{ overviewStats.success }}
              </div>
            </div>
            <div class="mini-stat">
              <div class="mini-k">{{ $t("Успешность") }}</div>
              <div class="mini-v">
                {{
                  overviewStats.total > 0
                    ? `${overviewStats.successRate.toFixed(1)}%`
                    : "—"
                }}
              </div>
            </div>
          </div>
        </template>
      </TabsContent>

      <TabsContent value="photos">
        <div v-if="loading" class="loading">
          <Spinner size="default" />
        </div>

        <template v-else>
          <input
            ref="fileInput"
            type="file"
            accept="image/*"
            multiple
            class="hidden"
            @change="onFilesSelected"
          />

          <div class="grid grid-cols-2 gap-2.5">
            <FacePhotoCell
              v-for="(image, index) in visibleImages"
              :key="image.id"
              :image="image"
              :number="index + 1"
              :loading="deletingId === image.id"
              :can-see-photo="isOwnUser"
              :revealable="isAdmin && !isOwnUser"
              size="lg"
              @photo-failed="onPhotoFailed"
            >
              <template v-if="isAdmin" #actions>
                <button
                  type="button"
                  class="inline-flex h-7 w-7 items-center justify-center rounded-md bg-black/60 text-white transition-colors hover:bg-red-500 disabled:opacity-50"
                  :disabled="uploading || deletingId !== null"
                  @click="removeFace(image.id)"
                >
                  <svg-icon name="huge-danger" class="h-3.5 w-3.5" />
                </button>
              </template>
            </FacePhotoCell>

            <button
              v-if="canUploadMore && personId && isAdmin"
              type="button"
              :disabled="uploading"
              class="flex aspect-[3/4] cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-[1.5px] border-dashed border-border bg-muted/40 text-muted-foreground transition-colors hover:border-blue-500 hover:bg-blue-50/40 hover:text-blue-600 disabled:opacity-50"
              @click="pickFiles"
            >
              <svg-icon name="huge-add-image" class="h-6 w-6" />
              <span class="font-medium">{{ $t("Загрузить") }}</span>
              <span class="text-[10px] text-muted-foreground/70">
                {{ $t("JPG, PNG · до 5МБ") }}
              </span>
            </button>
          </div>

          <div
            v-if="!images.length"
            class="mt-4 text-center text-sm text-muted-foreground"
          >
            {{ $t("Пока нет ни одной фотографии") }}
          </div>
        </template>
      </TabsContent>

      <TabsContent value="history">
        <div v-if="historyLoading" class="loading">
          <Spinner size="default" />
        </div>
        <div
          v-else-if="!history.length"
          class="text-muted-foreground text-sm py-8 text-center"
        >
          {{ $t("Нет данных") }}
        </div>
        <ul v-else class="history-list">
          <li v-for="item in history" :key="item.id" class="history-item">
            <div class="flex-1 flex flex-col gap-1 min-w-0">
              <div
                v-if="item[reasonKey] || item.reason_ru || item.reason"
                class="text-foreground line-clamp-2"
              >
                {{ item[reasonKey] || item.reason_ru || item.reason }}
              </div>
              <div class="flex justify-between items-center gap-2">
                <div class="flex items-center gap-2 min-w-0">
                  <UserAvatar
                    v-if="item.actor_name"
                    :seed="String(item.actor_id || item.actor_name)"
                    :name="item.actor_name"
                    :src="item.actor_image || ''"
                    :size="28"
                  />
                  <span
                    v-if="item.actor_name"
                    class="font-medium truncate"
                    :title="item.actor_name"
                  >
                    {{ item.actor_name }}
                  </span>
                  <Tag
                    :variant="item.status === 'success' ? 'success' : 'danger'"
                    size="sm"
                    class="rounded-md shrink-0"
                  >
                    {{
                      item.status === "success" ? $t("Успешно") : $t("Ошибка")
                    }}
                  </Tag>
                  <span
                    v-if="typeof item.similarity_score === 'number'"
                    class="text-muted-foreground shrink-0"
                  >
                    {{ formatScore(item.similarity_score) }}
                  </span>
                </div>
                <div class="text-gray-500 text-xs leading-5 shrink-0">
                  {{ formatTime(item.login_time) }}
                </div>
              </div>
            </div>
          </li>
        </ul>

        <div
          v-if="historyHasMore && !historyLoading"
          class="flex justify-center mt-3"
        >
          <Button
            variant="outline"
            size="sm"
            :disabled="historyLoadingMore"
            @click="loadMoreHistory"
          >
            {{ $t("Показать ещё") }}
          </Button>
        </div>
      </TabsContent>
    </Tabs>
  </div>
</template>

<script>
import Button from "@/shared/ui/button/Button.vue";
import Spinner from "@/shared/ui/spinner/Spinner.vue";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/shared/ui/tabs";
import { Tag } from "@/shared/ui/tag";
import FacePhotoCell from "@/components/face_recognition/FacePhotoCell.vue";
import UserAvatar from "@/pages/admin/settings/tabs/face-id/UserAvatar.vue";
import BaseTabs from "@/shared/ui/custom/tabs/BaseTabs.vue";
import { InformationLayout } from "@/shared/ui/information-layout";
import {
  fullName,
  formatDate,
  formatDateTime,
} from "@/pages/admin/settings/tabs/face-id/utils";
import {
  checkFacesExists,
  listPersonImages,
  enrollPerson,
  addFacesToPerson,
  deletePersonAllFaces,
  deleteFace,
  listPersonHistory,
} from "@/entities/face-recognition";

export default {
  name: "UserFaceRecognition",
  components: {
    Button,
    Spinner,
    FacePhotoCell,
    UserAvatar,
    Tabs,
    TabsList,
    TabsTrigger,
    TabsContent,
    Tag,
    BaseTabs,
    InformationLayout,
  },

  props: {
    selectedUser: { type: Object, default: null },
  },

  emits: ["update:has-faces", "update:uploading"],

  data() {
    return {
      loading: true,
      uploading: false,
      deletingId: null,
      hasFaces: false,
      images: [],
      maxImages: 4,
      activeTab: "overview",
      history: [],
      historyLoading: false,
      historyLoadingMore: false,
      historyOffset: 0,
      historyTotal: 0,
      historyLimit: 20,
      historyLoaded: false,
      failedObjectNames: [],
    };
  },

  computed: {
    currentLang() {
      return this.$i18n.locale;
    },
    reasonKey() {
      return {
        kk: "reason_kk",
        ru: "reason_ru",
      }[this.currentLang];
    },
    isAdmin() {
      return this.$store.state.self_user?.role === "ADMIN";
    },
    isOwnUser() {
      const self = this.$store.state.self_user || {};
      const selfId = String(self.uuid || self.id || "");
      const target = String(this.personId || "");
      return !!selfId && !!target && selfId === target;
    },
    personId() {
      return this.selectedUser?.uuid || this.selectedUser?.id || null;
    },
    personName() {
      const first = this.selectedUser?.first_name || "";
      const last = this.selectedUser?.last_name || "";
      const full = `${last} ${first}`.trim();
      return (
        full || this.selectedUser?.identifier || String(this.personId || "")
      );
    },
    emptySlots() {
      const left = Math.max(0, this.maxImages - this.images.length);
      return Array.from({ length: left }, (_, i) => i + 1);
    },
    visibleImages() {
      return (this.images || []).filter(
        (img) =>
          img &&
          img.object_name &&
          !this.failedObjectNames.includes(img.object_name)
      );
    },
    overviewImages() {
      return this.visibleImages.slice(0, 4);
    },
    canUploadMore() {
      return this.images.length < this.maxImages;
    },
    historyHasMore() {
      return this.history.length < this.historyTotal;
    },
    userName() {
      return this.selectedUser?.name || fullName(this.selectedUser);
    },
    userIdentifier() {
      return this.selectedUser?.identifier || "";
    },
    userEmail() {
      return this.selectedUser?.email || "";
    },
    overviewStats() {
      const total = Number.isFinite(this.historyTotal)
        ? this.historyTotal
        : this.history.length;
      const success = this.history.filter((h) => h.status === "success").length;
      const lastSeen = this.history[0]?.login_time || null;
      const successRate = total > 0 ? (success / total) * 100 : 0;
      return { total, success, successRate, lastSeen };
    },
    tabOptions() {
      return {
        overview: { label: this.$t("Обзор") },
        photos: {
          label: this.$t("Фотографии"),
          count: this.visibleImages.length,
        },
        history: {
          label: this.$t("История"),
          count: this.historyTotal || this.history.length,
        },
      };
    },
    infoSections() {
      const items = [
        {
          label: this.$t("ФИО"),
          values: [{ text: this.userName || "—" }],
        },
        {
          label: this.$t("Статус"),
          values: [
            {
              text: this.hasFaces ? this.$t("Активен") : this.$t("Не настроен"),
            },
          ],
        },
        {
          label: this.$t("ID пользователя"),
          values: [{ text: this.userIdentifier || this.personId || "—" }],
        },
        {
          label: "Email",
          values: [{ text: this.userEmail || "—" }],
        },
        {
          label: this.$t("Зарегистрирован"),
          values: [{ text: formatDate(this.selectedUser?.enrollment_date) }],
        },
        {
          label: this.$t("Последний вход"),
          values: [
            {
              text: this.overviewStats.lastSeen
                ? formatDateTime(this.overviewStats.lastSeen)
                : "—",
            },
          ],
        },
      ];
      if (this.selectedUser?.devices?.length) {
        items.push({
          label: this.$t("Устройства"),
          values: [{ text: this.selectedUser.devices.join(", ") }],
        });
      }
      return [{ items }];
    },
  },

  watch: {
    personId: {
      immediate: true,
      handler(value) {
        this.history = [];
        this.historyOffset = 0;
        this.historyTotal = 0;
        this.historyLoaded = false;
        if (value) {
          this.refresh();
          this.loadHistory(true);
        } else {
          this.loading = false;
        }
      },
    },
    hasFaces: {
      immediate: true,
      handler(value) {
        this.$emit("update:has-faces", value);
      },
    },
    uploading(value) {
      this.$emit("update:uploading", value);
    },
    activeTab(value) {
      if (
        (value === "history" || value === "overview") &&
        !this.historyLoaded &&
        this.personId
      ) {
        this.loadHistory(true);
      }
    },
  },

  methods: {
    onPhotoFailed(objectName) {
      if (!objectName) return;
      if (!this.failedObjectNames.includes(objectName)) {
        this.failedObjectNames.push(objectName);
      }
    },
    async refresh() {
      const pid = this.personId;
      if (!pid) {
        this.loading = false;
        return;
      }
      this.loading = true;
      this.failedObjectNames = [];
      try {
        let exists = false;
        try {
          const res = await checkFacesExists(pid);
          if (pid !== this.personId) return;
          exists = !!res?.has_faces;
        } catch {
          exists = false;
        }
        if (pid !== this.personId) return;
        this.hasFaces = exists;

        if (exists) {
          const res = await listPersonImages(pid);
          if (pid !== this.personId) return;
          this.images = this.sortImages(res?.images || []);
        } else {
          this.images = [];
        }
      } finally {
        if (pid === this.personId) this.loading = false;
      }
    },

    sortImages(list) {
      return [...(list || [])].sort((a, b) => {
        const ai = Number(a?.id);
        const bi = Number(b?.id);
        if (Number.isFinite(ai) && Number.isFinite(bi)) return ai - bi;
        return String(a?.id || "").localeCompare(String(b?.id || ""));
      });
    },

    pickFiles() {
      this.$refs.fileInput?.click();
    },

    async refreshImages() {
      const pid = this.personId;
      if (!pid) return;
      try {
        const res = await checkFacesExists(pid);
        if (pid !== this.personId) return;
        const exists = !!res?.has_faces;
        this.hasFaces = exists;
        if (exists) {
          const imgRes = await listPersonImages(pid);
          if (pid !== this.personId) return;
          this.images = this.sortImages(imgRes?.images || []);
          this.failedObjectNames = [];
        } else {
          this.images = [];
          this.failedObjectNames = [];
        }
      } catch {
        /* keep current state */
      }
    },

    async onFilesSelected(event) {
      if (!this.isAdmin) return;
      const selected = Array.from(event.target.files || []);
      event.target.value = "";
      if (!selected.length || !this.personId) return;

      const remaining = Math.max(0, this.maxImages - this.images.length);
      if (remaining <= 0) return;
      const files = selected.slice(0, remaining);

      this.uploading = true;
      try {
        if (this.hasFaces) {
          await addFacesToPerson(this.personId, files);
        } else {
          await enrollPerson({
            personId: String(this.personId),
            personName: this.personName,
            files,
          });
        }
        await this.refreshImages();
      } finally {
        this.uploading = false;
      }
    },

    async removeFace(faceId) {
      if (!this.isAdmin) return;
      if (!faceId) return;
      try {
        await this.$confirm(
          this.$t("Удалить это фото?").toString(),
          this.$t("Внимание").toString(),
          {
            confirmButtonText: this.$t("Да"),
            cancelButtonText: this.$t("Отмена"),
          }
        );
      } catch {
        return;
      }

      this.deletingId = faceId;
      try {
        await deleteFace(faceId);
        this.images = this.images.filter((img) => img.id !== faceId);
        if (!this.images.length) this.hasFaces = false;
      } finally {
        this.deletingId = null;
      }
    },

    async loadHistory(reset = false) {
      const pid = this.personId;
      if (!pid) return;
      if (reset) {
        this.history = [];
        this.historyOffset = 0;
        this.historyTotal = 0;
        this.historyLoading = true;
      } else {
        this.historyLoadingMore = true;
      }
      try {
        const res = await listPersonHistory(pid, {
          limit: this.historyLimit,
          offset: this.historyOffset,
        });
        if (pid !== this.personId) return;
        const items = res?.items || [];
        this.history = reset ? items : [...this.history, ...items];
        this.historyTotal = Number.isFinite(res?.total)
          ? res.total
          : this.history.length;
        this.historyOffset = this.history.length;
        this.historyLoaded = true;
      } catch {
        if (reset) this.history = [];
      } finally {
        if (pid === this.personId) {
          this.historyLoading = false;
          this.historyLoadingMore = false;
        }
      }
    },

    loadMoreHistory() {
      if (this.historyLoadingMore || !this.historyHasMore) return;
      this.loadHistory(false);
    },

    formatTime(value) {
      if (!value) return this.$t("Не указано");
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return String(value);
      return d.toLocaleString();
    },

    formatDate,
    formatDateTime,

    formatScore(score) {
      if (typeof score !== "number") return "";
      const pct = score <= 1 ? score * 100 : score;
      return `${pct.toFixed(1)}%`;
    },

    async removeAll() {
      if (!this.isAdmin) return;
      try {
        await this.$confirm(
          this.$t("Удалить все зарегистрированные лица?").toString(),
          this.$t("Внимание").toString(),
          {
            confirmButtonText: this.$t("Да"),
            cancelButtonText: this.$t("Отмена"),
            type: "warning",
          }
        );
      } catch {
        return;
      }

      this.uploading = true;
      try {
        await deletePersonAllFaces(this.personId);
        await this.refresh();
      } finally {
        this.uploading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.face-id-tab {
  @apply flex flex-col gap-2;
}

.loading {
  @apply flex items-center justify-center py-16 text-muted-foreground;
}

.group {
  @apply w-full flex flex-col justify-start items-start gap-4;

  ul {
    @apply w-full border border-solid border-border flex flex-col justify-start items-start overflow-hidden list-none m-0 p-0;
    border-radius: var(--radius);

    li {
      @apply w-full px-4 py-3 bg-card border-b border-border flex flex-col justify-center items-start gap-4;

      &:last-child {
        @apply border-b-0;
      }
    }
  }
}

.photo-slot {
  @apply relative flex items-center justify-center overflow-hidden  shrink-0;
  width: 60px;
  height: 60px;
  border-radius: var(--radius);

  &.filled {
    @apply border;
    border-color: hsl(var(--border));
    background-color: hsl(var(--muted));

    :deep(img) {
      @apply w-full h-full object-cover;
    }
  }

  &.empty {
    @apply flex-col gap-1.5 cursor-pointer transition-opacity p-2 border;
    border-color: hsl(var(--border));
    background-color: hsl(var(--muted));
    color: hsl(var(--primary));

    &:hover:not(:disabled) {
      opacity: 0.85;
    }

    &:disabled {
      @apply opacity-50 cursor-not-allowed;
    }

    .slot-icon {
      @apply w-4 h-4 opacity-80;
    }

    span {
      @apply text-sm font-medium;
    }
  }
}

.footer {
  @apply w-full flex justify-end;
}

.mini-stat {
  @apply flex flex-col gap-1  border border-border bg-card p-3;
  border-radius: var(--radius);
}

.mini-k {
  @apply text-[11px] font-medium uppercase tracking-wider text-muted-foreground;
}

.mini-v {
  @apply text-lg font-semibold;
}

.history-list {
  @apply w-full flex flex-col list-none m-0 p-0 border border-solid border-border overflow-hidden;
  border-radius: var(--radius);
}

.history-item {
  @apply w-full px-4 py-3 bg-card border-b border-border flex items-start gap-4;

  &:last-child {
    @apply border-b-0;
  }
}

.hidden {
  display: none;
}
</style>
